import { useNavigate, useParams } from 'react-router-dom';
import { StandardInput } from '../../../components/forms';
import { RadioButton } from '@progress/kendo-react-inputs';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { ComponentType, ReactNode, useContext, useMemo, useState } from 'react';

import {
  GetTimers,
  UpdateTaskAsync,
  GetResourceValue,
  UpdateGameDocState,
  GetTasksHasNoBoundary
} from '../../../utils/game-document';
import {
  TaskEntity,
  TimerEntity,
  ResourceEntity,
  TaskContentEntity
} from '../../../types/game-document/';
import TaskEditorFull from './task-editor-full';
import { TaskBasicView } from './task-basic-view';
import { EditorMode } from '../../../types/editor-mode';
import RequiredFields from '../../../types/required-fields';
import { ComboboxInput } from '../../../components/form-input';
import { UploadedImage } from '../image-resource/resource-window';
import { NavigationSource } from '../../../types/navigation-source';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TaskEvent } from '../../../types/game-document/entities/task';

interface TaskEditorProps {
  editorMode: EditorMode;
  navigationSource: NavigationSource;
  entity: TaskEntity;
  resources: ResourceEntity[];
  handleTaskChange: (
    field: keyof TaskEntity,
    value:
      | boolean
      | string
      | TaskEvent
      | { enablePremessageOutsideRange?: boolean; setTaskProximity?: number }
      | undefined
  ) => void;
  handleTaskResourceUpload?: (
    field: keyof TaskEntity,
    upload: UploadedImage
  ) => void;
  children?: ReactNode | undefined;
  imageResourceVisibility?: boolean | undefined;
  requiredFields?: RequiredFields<TaskEntity>[];
  //Existing task only show tasks that have no boundaries/coordinate
  showExistingTask?: boolean;
  handleExistingTask?: (taskId: string) => void;
  displayTimer?: boolean;
  displayTaskTitleSetting?: boolean;
  taskCoordinates?: { lat: number; lon: number };
  onCoordinateSave?: (lat: number, lon: number) => void;
}

export const TaskEditor: ComponentType<TaskEditorProps> = ({
  editorMode,
  navigationSource,
  entity,
  resources,
  handleTaskChange,
  handleTaskResourceUpload = () => {},
  imageResourceVisibility,
  requiredFields,
  showExistingTask,
  displayTimer,
  displayTaskTitleSetting,
  handleExistingTask = () => {},
  taskCoordinates,
  onCoordinateSave,
  ...props
}: TaskEditorProps) => {
  const navigate = useNavigate();
  const [state, setState] = useContext(GameDocumentContext);
  const [selectedTask, setSelectedTask] = useState<TaskEntity | undefined>(
    undefined
  );
  const { gameId, zoneId, taskId } = useParams();

  const taskContents = useMemo(() => {
    if (!state.gameDocument) return [];

    return (state.gameDocument.assets.taskContents ?? [])
      .filter((tc) => tc.onDemandTaskCategory === undefined)
      .map((tc) => ({
        ...tc,
        title:
          tc.id === '-'
            ? 'Create new'
            : GetResourceValue(state.gameDocument!, tc.titleResId ?? '')
      }));
  }, [state.gameDocument?.assets.taskContents]);

  const editTaskContent = () => {
    UpdateTaskAsync(state.gameDocument!, entity.id, entity, false).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));

        // navigate if selected existing task content
        if (navigationSource === 'map' && zoneId) {
          navigate(`/designer/${gameId}/map/${zoneId}/${entity.taskContentId}`);
        } else if (navigationSource === 'map' && !zoneId) {
          navigate(
            `/designer/${gameId}/map/task-content/${entity.taskContentId}`
          );
        } else {
          navigate(
            `/designer/${gameId}/assets/tasks/${taskId}/${entity.taskContentId}`
          );
        }
      }
    );
  };

  const editTimer = () => {
    UpdateTaskAsync(state.gameDocument!, entity.id, entity, false).then(
      (updatedGameDocument) => {
        setState((state) => UpdateGameDocState(state, updatedGameDocument));

        navigate(`/designer/${gameId}/assets/timers/${entity.timerAssId}`);
      }
    );
  };

  const timers = useMemo(() => {
    if (!state.gameDocument) return [];
    const emptyTimer = {
      id: '-',
      title: 'Create new',
      description: 'Test timer',
      titleResId: '-',
      direction: 'down',
      repeats: false,
      interval: 0
    };
    return [
      emptyTimer,
      ...GetTimers(state.gameDocument).map((t) => ({
        ...t,
        title: GetResourceValue(state.gameDocument!, t.titleResId ?? '')
      }))
    ];
  }, [state.gameDocument]);

  return (
    <>
      {showExistingTask && (
        <div
          className={'d-flex justify-content-space-between align-items-center'}>
          <ComboboxInput
            label={'Use existing task'}
            placeholder={'Select existing task'}
            data={GetTasksHasNoBoundary(state?.gameDocument).map((t) => ({
              ...t,
              title: GetResourceValue(state.gameDocument!, t.titleResId ?? '')
            }))}
            textField={'title'}
            name={'existing-task-id'}
            onChange={(e) => {
              if (!e.value) {
                setSelectedTask(undefined);
                handleExistingTask('');
              } else {
                setSelectedTask(e.target.value as TaskEntity);
                handleExistingTask(e?.target?.value?.id);
              }
            }}
          />
        </div>
      )}
      {selectedTask && <TaskBasicView entity={selectedTask} />}
      {!selectedTask && (
        <>
          {editorMode === 'basic' && (
            <>
              <StandardInput
                name={'titleResId'}
                className={'mb-2'}
                label={'Title'}
                value={
                  resources.find(
                    (resource) => resource?.id === entity.titleResId
                  )?.value ?? ''
                }
                validationMessage={
                  requiredFields?.find((task) => task.name === 'titleResId')
                    ?.errorMessage ?? ''
                }
                onChange={(e) =>
                  handleTaskChange('titleResId', e.target.value as string)
                }
                autoFocus
              />
              <StandardInput
                name={'description'}
                label={'Description'}
                value={entity.description}
                onChange={(e) =>
                  handleTaskChange('description', e.target.value as string)
                }
              />

              {displayTaskTitleSetting && (
                <div className={'d-flex align-items-center'}>
                  <span className={'mr-10 w-30'}>Title visibility</span>
                  <RadioButton
                    name={'taskTitleVisibility'}
                    value={true}
                    label={'Show'}
                    checked={entity.isTitleVisible}
                    onChange={(e) =>
                      handleTaskChange('isTitleVisible', e.value)
                    }
                  />

                  <RadioButton
                    name={'taskTitleVisibility'}
                    value={false}
                    label={'Hide'}
                    className={'ml-4'}
                    checked={!entity.isTitleVisible}
                    onChange={(e) =>
                      handleTaskChange('isTitleVisible', e.value)
                    }
                  />
                </div>
              )}

              <div className={'d-flex flex-column gap-2'}>
                <div className={'d-flex align-items-center gap-1'}>
                  <ComboboxInput
                    label={'Task content'}
                    placeholder={'Select task content'}
                    data={taskContents}
                    textField={'title'}
                    value={
                      entity.taskContentId &&
                      taskContents?.find((x) => x.id === entity.taskContentId)
                    }
                    name={'taskContentId'}
                    onChange={(e) =>
                      handleTaskChange(
                        'taskContentId',
                        (e.target.value as TaskContentEntity)?.id ?? ''
                      )
                    }
                  />
                  {entity.taskContentId && (
                    <div className="d-flex align-items-end pb-2 h-100 text-primary">
                      <span
                        className={
                          'material-symbols-outlined cursor-pointer text-decoration-none'
                        }
                        onClick={editTaskContent}>
                        edit_square
                      </span>
                    </div>
                  )}
                </div>
                {displayTimer && (
                  <div className={'d-flex align-items-center gap-1'}>
                    <ComboboxInput
                      label={'Timer'}
                      placeholder={'Select a timer'}
                      data={timers}
                      textField={'title'}
                      value={
                        entity.timerAssId &&
                        timers?.find((x) => x.id === entity.timerAssId)
                      }
                      onChange={(e: ComboBoxChangeEvent) => {
                        handleTaskChange(
                          'timerAssId',
                          (e.target.value as TimerEntity)?.id ?? ''
                        );
                      }}
                    />
                    {entity.timerAssId && (
                      <div className="d-flex align-items-end pb-2 h-100 text-primary">
                        <span
                          className={
                            'material-symbols-outlined cursor-pointer text-decoration-none'
                          }
                          onClick={editTimer}>
                          edit_square
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {editorMode === 'full' && (
            <TaskEditorFull
              navigationSource={'asset'}
              entity={entity}
              requiredFields={requiredFields}
              resources={resources}
              handleTaskChange={handleTaskChange}
              handleTaskResourceUpload={handleTaskResourceUpload}
              taskCoordinates={taskCoordinates}
              onCoordinateSave={onCoordinateSave}
            />
          )}

          {props.children}
        </>
      )}
    </>
  );
};
